import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { QUESTION_TYPES } from '@api/instant_survey';
import {
  Button,
  Checkbox,
  DEFAULT_CHART_COLORS,
  Error,
  FilterGroup,
  Layout,
  Legend,
  Lightbox,
  Loader,
  LoaderSkeleton,
  ResetFilterIcon,
  Select,
  truncateTextWithEllipsis,
} from '@utilities';

import ComparisonCharts from './components/ComparisonCharts';
import ConceptImageList from './components/ConceptImageList';
import ContSum from './components/ContSum';
import Grid from './components/Grid';
import KeyFindings from './components/KeyFindings';
import MaxDiff from './components/MaxDiff';
import NoDataPlaceholder from './components/NoDataPlaceholder';
import Numeric from './components/Numeric';
import OpenEnded from './components/OpenEnded';
import TextSummary from './components/TextSummary';
import Rank from './components/Rank';
import SortControls from './components/SortControls';
import Video from './components/Video';

import { DISPLAY_AS_OPTIONS, sanitizeQuestionText } from './utilities/helpers';
import { GATES } from '@constants';
import { SEARCH_PARAM_KEYS } from '../../../../utilities/constants';
import { getSummaryOrFindings } from '../../utilities/keyFindingsFilter';

import styles from './_index.module.scss';

const Responses = ({
  compareByOptions,
  displayAs,
  exportQuery,
  globalFilters,
  globalFiltersError,
  hideQuotaGroupFilters = false,
  isAskWhySurvey = false,
  isDataLoading,
  isFiltersLoading,
  isVideoSurvey,
  onDisplayAsChange,
  onQuestionSortByChange,
  onQuestionSortOrderChange,
  responseData,
  responseError,
  selectedCompareBy,
  sortByGroupByQuestion = {},
  sortOrderByQuestion = {},
  survey,
  surveyQuestions,
  surveyResponseSummariesMap,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conceptLightboxIndex, setConceptLightboxIndex] = useState(0);
  const [isConceptLightboxOpen, setIsConceptLightboxOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const forceRefetch = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    const key = '_r';
    const current = params.get(key);
    const next = current === '1' ? '0' : '1';
    params.set(key, next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const { client } = useStatsigClient();
  const showWholeSurveySummary = client.checkGate(GATES.WHOLE_SURVEY_SUMMARY);
  const isSortChartsEnabled = client.checkGate(GATES.SORT_CHARTS);

  const questionTypesThatCanOptionFilter = [
    QUESTION_TYPES.CHECKBOX.value,
    QUESTION_TYPES.RADIO.value,
  ];
  const canFilterByQuestionOptions = surveyQuestions?.some(({ type }) =>
    questionTypesThatCanOptionFilter.includes(type)
  );

  const { questionImageIndex, questionImages } = useMemo(() => {
    const images = [];
    const indexByQuestionId = {};

    surveyQuestions?.forEach((question, questionIndex) => {
      if (!question?.image?.cdnUrl || question?.showConceptImage) return;
      indexByQuestionId[question.id] = images.length;

      const caption = sanitizeQuestionText(
        question.title || question.text || `Question ${questionIndex + 1}`
      );
      images.push({
        alt: caption,
        caption,
        src: question.image.cdnUrl,
      });
    });

    return { questionImageIndex: indexByQuestionId, questionImages: images };
  }, [surveyQuestions]);

  const compareByGroups = useMemo(() => {
    switch (selectedCompareBy?.value) {
      case 'overall':
        return [{ color: DEFAULT_CHART_COLORS[0], id: 'Overall', name: 'Overall' }];
      case 'quota_group':
        return (
          globalFilters?.quotaGroups?.map(({ instantSurveyGroupRef, name }, index) => ({
            color: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
            id: instantSurveyGroupRef,
            name,
          })) || []
        );
      default:
        return (
          globalFilters?.demographicFilters
            ?.find(({ id }) => id === selectedCompareBy?.value)
            ?.compareBy.map(({ id, label }, index) => ({
              color: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
              id,
              name: label,
            })) || []
        );
    }
  }, [globalFilters, selectedCompareBy]);

  const quotaGroupConceptImages = useMemo(() => {
    if (selectedCompareBy?.value !== 'quota_group' || !survey?.groups?.length) return [];

    return compareByGroups
      .map((compareByGroup) => {
        const compareId = String(compareByGroup?.id ?? '');
        const group = survey.groups.find(
          ({ id, name, quotaGroupRef }) =>
            String(quotaGroupRef) === compareId ||
            String(id) === compareId ||
            name === compareByGroup?.name
        );

        if (!group?.conceptImage?.cdnUrl) return null;

        const conceptName = group?.conceptName || group?.name || compareByGroup?.name || 'Concept';
        const quotaGroupName = compareByGroup?.name || group?.name || conceptName;

        return {
          alt: `Concept for ${group?.name || compareByGroup?.name || 'quota group'}`,
          caption: conceptName,
          color: compareByGroup?.color,
          id: compareByGroup?.id,
          name: conceptName,
          quotaGroupName,
          src: group.conceptImage.cdnUrl,
        };
      })
      .filter(Boolean);
  }, [compareByGroups, selectedCompareBy?.value, survey?.groups]);

  const conceptLightboxImages = useMemo(
    () =>
      quotaGroupConceptImages.map(({ alt, caption, src }) => ({
        alt,
        caption,
        src,
      })),
    [quotaGroupConceptImages]
  );

  useEffect(() => {
    if (!quotaGroupConceptImages.length) return;
    const preloadImages = quotaGroupConceptImages.map(({ src }) => {
      const image = new Image();
      image.src = src;
      return image;
    });

    return () => {
      preloadImages.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [quotaGroupConceptImages]);

  const handleOpenLightbox = useCallback(
    (questionId) => {
      const imageIndex = questionImageIndex[questionId];
      if (imageIndex === undefined) return;
      setLightboxIndex(imageIndex);
      setIsLightboxOpen(true);
    },
    [questionImageIndex]
  );

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  const handleOpenConceptLightbox = useCallback((imageIndex) => {
    if (imageIndex === undefined) return;
    setConceptLightboxIndex(imageIndex);
    setIsConceptLightboxOpen(true);
  }, []);

  const handleCloseConceptLightbox = () => {
    setIsConceptLightboxOpen(false);
  };

  const areAnyFiltersSelected = useMemo(() => {
    const {
      [SEARCH_PARAM_KEYS.QUESTION_OPTION]: selectedOptions,
      [SEARCH_PARAM_KEYS.QUOTA_GROUP]: selectedGroups,
      ...otherParams
    } = Object.fromEntries(searchParams);

    return (
      globalFilters?.demographicFilters?.some(
        ({ id }) => otherParams[id]?.split(',')?.length > 0
      ) ||
      selectedOptions?.split(',')?.length > 0 ||
      selectedGroups?.split(',')?.length > 0
    );
  }, [globalFilters, searchParams]);

  const selectedQuotaGroups = useMemo(() => {
    const groupSearchParam = searchParams.get(SEARCH_PARAM_KEYS.QUOTA_GROUP);
    if (groupSearchParam === null)
      return globalFilters?.quotaGroups?.map(({ instantSurveyGroupRef }) => instantSurveyGroupRef);
    return groupSearchParam.split(',').filter(Boolean);
  }, [globalFilters, searchParams]);

  const supportedQuestionTypes = isVideoSurvey
    ? [QUESTION_TYPES.VIDEO.value]
    : [
        QUESTION_TYPES.CHECKBOX.value,
        QUESTION_TYPES.CONT_SUM.value,
        QUESTION_TYPES.ESSAY.value,
        QUESTION_TYPES.MAX_DIFF.externalType,
        QUESTION_TYPES.NUMERIC.externalType,
        QUESTION_TYPES.RADIO.value,
        QUESTION_TYPES.RANK.value,
        QUESTION_TYPES.TABLE.value,
      ];
  const handleFilterChange = ({ filter, value }) => {
    const values = Object.keys(value).filter((key) => value[key]);
    if (!values.length) {
      searchParams.delete(filter.id);
    } else {
      searchParams.set(filter.id, values.join());
    }
    setSearchParams(searchParams);
  };

  const handleGroupFilterChange = ({ instantSurveyGroupRef }) => {
    const currentSelectedGroups = [...selectedQuotaGroups];
    if (currentSelectedGroups.includes(instantSurveyGroupRef)) {
      currentSelectedGroups.splice(currentSelectedGroups.indexOf(instantSurveyGroupRef), 1);
    } else {
      currentSelectedGroups.push(instantSurveyGroupRef);
    }
    searchParams.set(SEARCH_PARAM_KEYS.QUOTA_GROUP, currentSelectedGroups.join());
    setSearchParams(searchParams);
  };

  const handleQuestionFilterChange = ({ filter, value }) => {
    const selectedOptions =
      searchParams
        .get(SEARCH_PARAM_KEYS.QUESTION_OPTION)
        ?.split(',')
        .filter((value) => value) || []; // filter out possible errant empty string `''`
    Object.keys(value).forEach((optionId) => {
      if (value[optionId] && !selectedOptions.includes(optionId)) {
        selectedOptions.push(optionId);
      } else if (!value[optionId] && selectedOptions.includes(optionId)) {
        selectedOptions.splice(selectedOptions.indexOf(optionId), 1);
      }
    });

    if (!selectedOptions.length) {
      searchParams.delete(SEARCH_PARAM_KEYS.QUESTION_OPTION);
    } else {
      searchParams.set(SEARCH_PARAM_KEYS.QUESTION_OPTION, selectedOptions.join());
    }
    setSearchParams(searchParams);
  };

  const handleResetFilters = () => {
    searchParams.delete(SEARCH_PARAM_KEYS.QUESTION_OPTION);
    searchParams.delete(SEARCH_PARAM_KEYS.QUOTA_GROUP);
    globalFilters?.demographicFilters?.forEach(({ id }) => searchParams.delete(id));
    setSearchParams(searchParams);
  };

  const getCanQuestionSort = ({
    isSortChartsEnabled,
    question,
    questionData,
    sortOrderByQuestion,
  }) => {
    const isTurf = questionData?.turfAnalysis != null;
    const isSortableType =
      question?.type === QUESTION_TYPES.CHECKBOX.value ||
      question?.type === QUESTION_TYPES.RADIO.value;
    const overallSeries =
      questionData?.chartPct?.series?.[0] ?? questionData?.chartCount?.series?.[0];
    const hasEnoughBars = (overallSeries?.data?.length ?? 0) > 1;

    // determine if we can sort charts for this question
    const canSortCharts = isSortableType && !isTurf && isSortChartsEnabled && hasEnoughBars;
    const sortOrderForQuestion = sortOrderByQuestion[question.id] ?? 'default';
    return { canSortCharts, sortOrderForQuestion };
  };

  const renderSummaryOrFindingsForQuestion = ({ question, segment }) => {
    const result = getSummaryOrFindings({
      areFiltersApplied: areAnyFiltersSelected,
      question,
      segment,
      selectedCompareBy,
      surveyResponseSummariesMap,
    });

    if (!result) return null;

    // Determine component based on question type
    if (question.type === QUESTION_TYPES.ESSAY.value) {
      return <TextSummary data={result} />;
    }

    if (question.type === QUESTION_TYPES.VIDEO.value) {
      return <TextSummary data={result} />;
    }

    return <KeyFindings data={result} />;
  };

  const renderQuestion = (question, sortOrderForQuestion) => {
    if (!supportedQuestionTypes.includes(question.type))
      return <p>No chart available for this question type.</p>;
    const questionData = responseData?.[question.id];
    const questionOptions = question?.answers || question?.options || [];
    const sortByGroupForQuestion = sortByGroupByQuestion[question.id] ?? 'default';
    if (!questionData) return <NoDataPlaceholder />;

    switch (question.type) {
      case QUESTION_TYPES.CHECKBOX.value:
      case QUESTION_TYPES.RADIO.value:
        return (
          <ComparisonCharts
            compareByGroups={compareByGroups}
            compareByValue={selectedCompareBy?.value}
            conceptImages={question?.showConceptImage ? quotaGroupConceptImages : []}
            data={questionData}
            displayAs={displayAs.value}
            exportQuery={exportQuery}
            globalFilters={globalFilters}
            isAskWhySurvey={isAskWhySurvey}
            onOpenConceptImage={handleOpenConceptLightbox}
            onOpenLightbox={handleOpenLightbox}
            onTop2BoxApplied={forceRefetch}
            question={question}
            questionOptions={questionOptions}
            renderKeyFindings={renderSummaryOrFindingsForQuestion}
            sortByValue={
              selectedCompareBy?.value === 'overall' ? 'default' : sortByGroupForQuestion
            }
            sortOrder={sortOrderForQuestion}
            survey={survey}
          />
        );
      case QUESTION_TYPES.CONT_SUM.value:
        return (
          <ContSum
            compareByGroups={compareByGroups}
            data={questionData}
            displayAs={displayAs.value}
          />
        );
      case QUESTION_TYPES.ESSAY.value:
        return (
          <OpenEnded
            data={questionData}
            question={question}
            renderResponseTextSummary={renderSummaryOrFindingsForQuestion}
          />
        );
      case QUESTION_TYPES.MAX_DIFF.externalType:
        return (
          <MaxDiff
            data={questionData}
            displayAs={displayAs.value}
            questionOptions={questionOptions}
          />
        );
      case QUESTION_TYPES.NUMERIC.externalType:
        return (
          <Numeric
            compareByGroups={compareByGroups}
            data={questionData}
            displayAs={displayAs.value}
          />
        );
      case QUESTION_TYPES.RANK.value:
        return (
          <Rank
            data={questionData}
            displayAs={displayAs.value}
            question={question}
            questionOptions={questionOptions}
          />
        );
      case QUESTION_TYPES.TABLE.value:
        return (
          <Grid
            compareByGroups={compareByGroups}
            data={questionData}
            displayAs={displayAs.value}
            isAskWhySurvey={isAskWhySurvey}
            onTop2BoxApplied={forceRefetch}
            question={question}
            selectedCompareBy={selectedCompareBy}
            surveyId={survey?.id}
          />
        );
      case QUESTION_TYPES.VIDEO.value:
        return (
          <Video
            data={questionData}
            question={question}
            renderResponseTextSummary={renderSummaryOrFindingsForQuestion}
          />
        );
      default:
        return null;
    }
  };

  if (globalFiltersError) return <Error status={globalFiltersError?.response?.status} />;
  if (isFiltersLoading) return <Loader isCentered />;

  const wholeSurvey = surveyResponseSummariesMap.wholeSurveySummary;

  return (
    <Layout.Flex className={styles['responses']} gap="large">
      <Layout.Sidebar className={styles['responses-sidebar']}>
        <Layout.Flex.Column gap="x-large">
          <div className={styles['responses-compare-by']}>
            <Select
              className={styles['responses-filter-select']}
              label="Compare By"
              onChange={({ value }) => {
                searchParams.set(SEARCH_PARAM_KEYS.COMPARE_BY, value);
                setSearchParams(searchParams);
              }}
              options={compareByOptions}
              value={selectedCompareBy}
            />
            <Legend items={compareByGroups} />
          </div>

          {!isVideoSurvey && (
            <Select
              className={styles['responses-filter-select']}
              label="Display Data In"
              onChange={onDisplayAsChange}
              options={DISPLAY_AS_OPTIONS}
              value={displayAs}
            />
          )}

          {globalFilters?.demographicFilters && (
            <Layout.Flex.Column gap="small">
              <h4>Demographic Filters</h4>
              {globalFilters.demographicFilters.map(({ options, ...props }) => {
                const filter = {
                  options: options.map((option) => ({
                    helpText: `(${option.count})`,
                    ...option,
                  })),
                  ...props,
                };
                const value = searchParams
                  .get(filter.id)
                  ?.split(',')
                  .reduce((acc, prop) => ({ ...acc, [prop]: true }), {});
                return (
                  <FilterGroup
                    filter={filter}
                    key={filter.id}
                    onChange={(newValue) => handleFilterChange({ filter, value: newValue })}
                    value={value}
                  />
                );
              })}
            </Layout.Flex.Column>
          )}

          {!hideQuotaGroupFilters && globalFilters?.quotaGroups?.length > 1 && (
            <Layout.Flex.Column gap="small">
              <h4>Quota Group Filters</h4>
              {globalFilters?.quotaGroups.map(({ instantSurveyGroupRef, name }) => (
                <Checkbox
                  id={`group-filter-${instantSurveyGroupRef}`}
                  isChecked={selectedQuotaGroups.includes(instantSurveyGroupRef)}
                  key={instantSurveyGroupRef}
                  label={truncateTextWithEllipsis({ length: 20, text: name })}
                  onChange={() => handleGroupFilterChange({ instantSurveyGroupRef })}
                  title={name}
                  value={instantSurveyGroupRef}
                />
              ))}
            </Layout.Flex.Column>
          )}

          {!isVideoSurvey && canFilterByQuestionOptions && (
            <Layout.Flex.Column gap="small">
              <h4>Question Filters</h4>
              {surveyQuestions
                ?.map((question, index) => ({ ...question, index })) // preserve pre-filtered index
                .filter(({ type }) => questionTypesThatCanOptionFilter.includes(type))
                .map(({ id, index, options, title, type }) => ({
                  id,
                  label: `Q${index + 1}. ${title}`,
                  options: options?.map(({ id, title }) => ({
                    id,
                    label: title,
                  })),
                  title,
                  type,
                }))
                .map((filter) => {
                  const value = searchParams
                    .get(SEARCH_PARAM_KEYS.QUESTION_OPTION)
                    ?.split(',')
                    .reduce((acc, prop) => ({ ...acc, [prop]: true }), {});

                  return (
                    <FilterGroup
                      filter={filter}
                      key={filter.id}
                      onChange={(newValue) =>
                        handleQuestionFilterChange({ filter, value: newValue })
                      }
                      value={value}
                    />
                  );
                })}
            </Layout.Flex.Column>
          )}

          <Button
            className={styles['responses-filter-reset']}
            icon={<ResetFilterIcon />}
            isDisabled={!areAnyFiltersSelected}
            onClick={handleResetFilters}
            text="Reset Filters"
            variant="secondary"
          />
        </Layout.Flex.Column>
      </Layout.Sidebar>

      <Layout.Fill as={Layout.Flex.Column} className={styles['responses-main']} gap="x-large">
        {showWholeSurveySummary && wholeSurvey && (
          <div className={styles['responses-whole-summary']}>
            <TextSummary data={wholeSurvey} header="Overall Summary" />
          </div>
        )}
        {responseError && <Error status={responseError?.response?.status} />}
        {!responseError &&
          surveyQuestions?.map((question, index) => {
            const questionData = responseData?.[question.id];
            const sortByGroupForQuestion = sortByGroupByQuestion[question.id] ?? 'default';
            const { canSortCharts, sortOrderForQuestion } = getCanQuestionSort({
              isSortChartsEnabled,
              question,
              questionData,
              sortOrderByQuestion,
            });
            const isSortableType =
              question?.type === QUESTION_TYPES.CHECKBOX.value ||
              question?.type === QUESTION_TYPES.RADIO.value;

            const isQuestionImageDisplayed =
              question?.image?.cdnUrl && !question?.showConceptImage && !isSortableType;
            const shouldShowConceptImages =
              question?.showConceptImage && quotaGroupConceptImages.length > 0;
            const showConceptImagesInResponses = shouldShowConceptImages && !isSortableType;
            const questionLabel = sanitizeQuestionText(
              question.title || question.text || `Question ${index + 1}`
            );

            const sanitizedQuestion = {
              ...question,
              text: sanitizeQuestionText(question.text),
              title: sanitizeQuestionText(question.title),
            };

            return (
              <Fragment key={question.id}>
                <div className={styles['responses-question']}>
                  <h3 className={styles['responses-question-text']}>
                    Q{index + 1}. {questionLabel}
                  </h3>
                  {canSortCharts && (
                    <SortControls
                      compareByGroups={compareByGroups}
                      compareByValue={selectedCompareBy?.value}
                      isDisabled={isDataLoading}
                      onSortByChange={(newSortBy) =>
                        onQuestionSortByChange?.(question.id, newSortBy)
                      }
                      onSortOrderChange={(newOrder) =>
                        onQuestionSortOrderChange?.(question.id, newOrder)
                      }
                      sortByValue={sortByGroupForQuestion}
                      sortOrder={sortOrderForQuestion}
                    />
                  )}
                  {isDataLoading && (
                    <LoaderSkeleton height={500}>
                      <rect height="30" rx="2" ry="2" width="250" x="0" y="10" />
                      <rect height="450" rx="2" ry="2" width="1000" x="0" y="50" />
                    </LoaderSkeleton>
                  )}
                  {!isDataLoading && (
                    <>
                      {isQuestionImageDisplayed && (
                        <button
                          aria-label={`Open image for ${questionLabel || 'question'}`}
                          className={styles['responses-question-image-button']}
                          onClick={() => handleOpenLightbox(question.id)}
                          type="button"
                        >
                          <img
                            alt={questionLabel || 'Question image'}
                            className={styles['responses-question-image']}
                            src={question.image.cdnUrl}
                          />
                        </button>
                      )}
                      {showConceptImagesInResponses && (
                        <ConceptImageList
                          className={styles['responses-concept-image-list']}
                          items={quotaGroupConceptImages}
                          onOpen={handleOpenConceptLightbox}
                        />
                      )}
                      {renderQuestion(sanitizedQuestion, sortOrderForQuestion)}
                    </>
                  )}
                </div>
                {index !== surveyQuestions.length - 1 && <hr />}
              </Fragment>
            );
          })}
      </Layout.Fill>

      {questionImages.length > 0 && (
        <Lightbox
          currentImageIndex={lightboxIndex}
          images={questionImages}
          isActive={isLightboxOpen}
          onClose={handleCloseLightbox}
        />
      )}

      {conceptLightboxImages.length > 0 && (
        <Lightbox
          currentImageIndex={conceptLightboxIndex}
          images={conceptLightboxImages}
          isActive={isConceptLightboxOpen}
          onClose={handleCloseConceptLightbox}
        />
      )}
    </Layout.Flex>
  );
};

Responses.propTypes = {
  compareByOptions: PropTypes.array.isRequired,
  displayAs: PropTypes.object.isRequired,
  exportQuery: PropTypes.object,
  globalFilters: PropTypes.object,
  globalFiltersError: PropTypes.object,
  hideQuotaGroupFilters: PropTypes.bool,
  isAskWhySurvey: PropTypes.bool,
  isDataLoading: PropTypes.bool,
  isFiltersLoading: PropTypes.bool,
  isVideoSurvey: PropTypes.bool,
  onDisplayAsChange: PropTypes.func,
  onQuestionSortByChange: PropTypes.func,
  onQuestionSortOrderChange: PropTypes.func,
  responseData: PropTypes.object,
  responseError: PropTypes.object,
  selectedCompareBy: PropTypes.object,
  sortByGroupByQuestion: PropTypes.object,
  sortOrderByQuestion: PropTypes.object,
  survey: PropTypes.object,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
  surveyResponseSummariesMap: PropTypes.object,
};

export default Responses;
